import { If } from "@consumidor-positivo/aurora";
import { useMemo, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAuthentication } from "@libs/acp/contexts/Authentication";
import { getPathNamePermissions } from "@libs/acp/utils/getPathNameAccessPermissions";

import useNavigationGuard from "@libs/acp/hooks/useNavigationGuard";

import useAuthenticatedUser from "@libs/acp/hooks/useAuthenticatedUser";

import { DebtsProvider } from "@dues/contexts/DebtsContext/DebtsProvider";
import { Footer } from "@shared/components/Footer";
import { LoggedInHeader } from "@shared/components/LoggedInHeader";
import { fullScreenPagePaths, pageNames } from "@shared/routes/pageNames";
import PageLoader from "@shared/ui/PageLoader";

function ACPNavigationObserver() {
	useNavigationGuard();
	return null;
}

export default function PrivateRoutes() {
	const hasTriggeredValidateCredentialsRedirect = useRef(false);

	const { isAuthenticated, isLoading, customer } = useAuthentication();

	const { hasPermission } = useAuthenticatedUser();

	const location = useLocation();
	const { pathname } = location;

	const canAccess = useMemo(() => {
		if (hasPermission("all")) return true;

		const currentPathPermissions = getPathNamePermissions(pageNames, pathname);

		const userPermissions: string[] = [];
		currentPathPermissions?.forEach((permission) => {
			if (hasPermission(permission)) userPermissions.push(permission);
		});

		return userPermissions.length > 0;
	}, [pathname]);

	if (isLoading) return <PageLoader />;

	if (!isAuthenticated && !isLoading) {
		return (
			<Navigate
				to={{ pathname: pageNames.signin.path, search: window.location.search }}
				replace
			/>
		);
	}

	const mustValidateCredentials =
		!!isAuthenticated &&
		!pathname.includes(pageNames.myDebts.path) &&
		!canAccess &&
		!hasTriggeredValidateCredentialsRedirect.current;

	if (mustValidateCredentials) {
		hasTriggeredValidateCredentialsRedirect.current = true;
		return (
			<Navigate
				to={{ pathname: pageNames.unlockProtectedFeature.path }}
				replace
				state={{ featureSlug: pathname }}
			/>
		);
	}
	hasTriggeredValidateCredentialsRedirect.current = false;

	const isFullScreen = fullScreenPagePaths.includes(pathname);

	return (
		<DebtsProvider>
			<ACPNavigationObserver />
			<If condition={!isFullScreen}>
				<LoggedInHeader username={customer?.full_name} />
			</If>
			<Outlet />
			<If condition={!isFullScreen}>
				<Footer />
			</If>
		</DebtsProvider>
	);
}
